import { ERROR_API_CALL_FAILED } from '../../../common/framework/constants';
import { apiGet, apiPost, apiPut } from '../../framework/client';
import { buildQuery } from '../../framework/client/resource';
import { VehicleContract } from '../../../common/application/model/VehicleContract';
import { MonthlyCostRowMap } from '../../../common/application/model/MonthlyCostRowMap';
import { VendorContractMap } from '../../../common/application/model/VendorContractMap';
import { VehicleContractNumberMap } from '../../../common/application/model/VehicleContractNumberMap';
import { MoveVehicleContractReq } from '../../../common/application/model/api/MoveVehicleContractReq';
import { MoveVehicleContractResp } from '../../../common/application/model/api/MoveVehicleContractResp';
import { EndVehicleContractReq } from '../../../common/application/model/api/EndVehicleContractReq';
import { EndVehicleContractResp } from '../../../common/application/model/api/EndVehicleContractResp';
import { EndAndMoveVehicleContractResp } from '../../../common/application/model/api/EndAndMoveVehicleContractResp';

export const getVehicleContractCounts = async (): Promise<any> => {
    const response = await apiGet('/api/admin/count/vehicle-contracts');
    if (response.status === 200) {
        return (await response.json()) as any;
    } else {
        throw new Error(ERROR_API_CALL_FAILED);
    }
};

export const getFinancingContractIdByContractNumber = async (contractNumber: string): Promise<any> => {
    const response = await apiGet(`/api/financing-contract/${contractNumber}`);

    if (response.status === 200) {
        return (await response.json()) as any;
    } else {
        throw new Error(ERROR_API_CALL_FAILED);
    }
};

export const findVehicleContractWithRegistrationNumber = async (
    vehicleRegistrationNumber: string,
): Promise<{ id: string | undefined }> => {
    const response = await apiGet(`/api/public/find-vehicle-contract/${vehicleRegistrationNumber}`);
    if (response.status === 200) {
        return (await response.json()) as any;
    } else {
        throw new Error(ERROR_API_CALL_FAILED);
    }
};

export const updateMonthlyRowsForVehicleContract = async (
    customerId: string,
    vehicleContractId: string,
): Promise<boolean> => {
    const response = await apiGet(`/api/update-monthly-rows/${customerId}/${vehicleContractId}`);
    if (response.status === 200) {
        return (await response.json()) as any;
    } else {
        throw new Error(ERROR_API_CALL_FAILED);
    }
};

export const getVehicleContracts = async (
    parameters: Map<string, string>,
    customPart?: string,
): Promise<{
    vehicleContracts: VehicleContract[];
    monthlyCostRowMap: MonthlyCostRowMap;
    vendorContractMap: VendorContractMap;
    vehicleContractNumbers: VehicleContractNumberMap;
}> => {
    let body: any = {};
    let queryParameters = '';

    const pagingParameters = new Map<string, string>();
    let queryString = ``;

    if (parameters) {
        const query = buildQuery(parameters, queryString, body, pagingParameters, customPart);
        queryParameters = query.queryParameters;
        body = query.body;
    }

    const response = await apiPost(`/api/manager/vehicle-contract${queryParameters}`, body);
    if (response.status === 200) {
        return (await response.json()) as any;
    } else {
        throw new Error(ERROR_API_CALL_FAILED);
    }
};

export const moveVehicleContract = async (
    newContractData: MoveVehicleContractReq,
): Promise<MoveVehicleContractResp> => {
    const response = await apiPost(`/api/manager/move-vehicle-contract/`, newContractData);
    if (response.status === 200) {
        return (await response.json()) as MoveVehicleContractResp;
    } else {
        throw new Error(ERROR_API_CALL_FAILED);
    }
};

export const endAndMoveVehicleContract = async (
    newContractData: MoveVehicleContractReq,
): Promise<EndAndMoveVehicleContractResp> => {
    const response = await apiPost(`/api/manager/end-and-move-vehicle-contract/`, newContractData);
    if (response.status === 200) {
        return (await response.json()) as EndAndMoveVehicleContractResp;
    } else {
        throw new Error(ERROR_API_CALL_FAILED);
    }
};

export async function endVehicleContract(endContractReq: EndVehicleContractReq): Promise<EndVehicleContractResp> {
    const response = await apiPost(`/api/manager/end-vehicle-contract/`, endContractReq);
    if (response.status === 200) {
        return (await response.json()) as EndVehicleContractResp;
    } else {
        throw new Error(ERROR_API_CALL_FAILED);
    }
}

export const parseTraficomCsv = async (fileName: string, customerId: string): Promise<VehicleContract[]> => {
    const response = await apiGet(`/api/admin/parse-traficom-csv`, { fileName, customerId });
    if (response.status === 200) {
        return (await response.json()) as VehicleContract[];
    } else {
        throw new Error(ERROR_API_CALL_FAILED);
    }
};

export const importCustomerFleetFromTraficomCsv = async (
    vehicleContracts: any[],
): Promise<{
    vehicleContracts: {
        updated: VehicleContract[];
        added: VehicleContract[];
        failed: Array<VehicleContract & { errorText: string }>;
    };
}> => {
    const response = await apiPost('/api/admin/import-traficom-csv', { vehicleContracts });
    if (response.status === 200) {
        return await response.json();
    } else {
        throw new Error(ERROR_API_CALL_FAILED);
    }
};

export const updateVehicleInspectionDates = async (
    vehicleContracts: VehicleContract[],
): Promise<{ updated: number }> => {
    const response = await apiPut(`/api/manager/update-vehicle-inspection-dates`, { vehicleContracts });
    if (response.status === 200) {
        return (await response.json()) as { updated: number };
    } else {
        throw new Error(ERROR_API_CALL_FAILED);
    }
};

export const exportVehicleContracts = async (
    parameters: Map<string, string>,
    customPart?: string,
): Promise<{
    fileName: string;
}> => {
    let body: any = {};
    let queryParameters = '';

    const pagingParameters = new Map<string, string>();
    let queryString = ``;

    if (parameters) {
        const query = buildQuery(parameters, queryString, body, pagingParameters, customPart);
        queryParameters = query.queryParameters;
        body = query.body;
    }

    const response = await apiPost(`/api/manager/vehicle-contract/export${queryParameters}`, body);

    if (response.status === 200) {
        return (await response.json()) as any;
    } else {
        throw new Error(ERROR_API_CALL_FAILED);
    }
};

export const getMonthlyNumbersVehicleContracts = async (parameters: Map<string, string>,  specialQueryParameters?: Map<string, any>): Promise<any> => {
    let body: any = {};

    const pagingParameters = new Map<string, string>();
    let queryString = ``;

    if (parameters) {
        const query = buildQuery(parameters, queryString, body, pagingParameters, '');
        body = query.body;
    }

    const queryParameters = specialQueryParameters
        ? '?' + Array.from(specialQueryParameters, ([key, value]) => encodeURI(key) + '=' + encodeURI(value)).join('&')
        : '';

    const response = await apiPost(
        `/api/manager/monthly-numbers-vehicle-contracts${queryParameters}`,
        body,
    );

    if (response.status === 200) {
        return (await response.json()) as any;
    } else {
        throw new Error(ERROR_API_CALL_FAILED);
    }
};

export const getMonthlyRealisationPrice = async (parameters: Map<string, string>,  specialQueryParameters?: Map<string, any>): Promise<any> => {
    let body: any = {};

    const pagingParameters = new Map<string, string>();
    let queryString = ``;

    if (parameters) {
        const query = buildQuery(parameters, queryString, body, pagingParameters, '');
        body = query.body;
    }

    const queryParameters = specialQueryParameters
        ? '?' + Array.from(specialQueryParameters, ([key, value]) => encodeURI(key) + '=' + encodeURI(value)).join('&')
        : '';

    const response = await apiPost(
        `/api/manager/monthly-realisation-price${queryParameters}`,
        body,
    );

    if (response.status === 200) {
        return (await response.json()) as any;
    } else {
        throw new Error(ERROR_API_CALL_FAILED);
    }
};
